import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { useScheduleAndSensorStatusesForRequest } from "@lib/logic";
import type { MeetingRoom } from "@lib/store";
import { pickUsingStringAsIndex } from "@lib/utils";
import { useRoomImage } from "@hooks";
import { ModalCellItemIcon } from "@molecules";
import { ScheduleDataIcon } from "src/components/molecules/ScheduleDataIcon";

import { useRoomsRequest } from "./useRoomsRequest";

// The picture is drawn at 48px, so it's requested at a few times that for sharp screens
const IMAGE_WIDTH = Math.round(48 * window.devicePixelRatio * 4);

/** A room in the list and the carousel: its picture, seats and floor, and how busy it is by sensor and by calendar. */
export const RoomCell = ({ room }: { room: MeetingRoom }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const request = useRoomsRequest();
  const imageURL = useRoomImage(room.email || room.displayName, room.roomPictureHash, room.buildingId, IMAGE_WIDTH);
  const { scheduleStatus, sensorStatus, sublineText } = useScheduleAndSensorStatusesForRequest(
    request,
    room.id,
    room.email,
  );

  return (
    <div className="flex w-full items-center justify-between gap-2">
      <div className="flex min-w-0 items-start gap-3">
        <ModalCellItemIcon
          className="flex-none self-start"
          imageURL={imageURL}
          backgroundColor={pickUsingStringAsIndex(room.email, theme.avatar.colors)}
          icon="meetingRoom"
        />
        <div className="flex min-w-0 flex-col justify-center pt-0.5">
          <p className="truncate font-semibold">{room.displayName}</p>
          <p className="truncate text-mapiq-black-500">
            {t("meeting:MeetingRoomBasicInfo", { count: room.capacity, floorName: room.floorName })}
          </p>
          {sublineText && <p className="truncate text-functional-red">{sublineText}</p>}
        </div>
      </div>

      <div className="flex flex-none gap-2">
        {sensorStatus && (
          <ScheduleDataIcon
            data-testid={`HaNRoomSensorStatus-${room.id}`}
            status={sensorStatus}
            icon="broadcast"
          />
        )}
        {scheduleStatus && (
          <ScheduleDataIcon
            data-testid={`HaNRoomScheduleStatus-${room.id}`}
            status={scheduleStatus}
            icon="calendar"
          />
        )}
      </div>
    </div>
  );
};
